import React, { useMemo } from "react";
import moment from "moment";
import styled from "styled-components";
import { colors2, getVal, WrapperCardGrid } from "oolib";
import {
  UI_BODY_SEMIBOLD_SM,
  UI_CAPTION,
} from "oolib/dist/v2/components/Typo2";
import { getTenant } from "@/TENANT_CONFIGS";

import { LineChart } from "@/components/dataviz/LineChartV2";
import { _base_linechart_ui_configs } from "@/components/dataviz/LineChartV2/_base_linechart_ui_configs";
import { mad_useChildDataOptions } from "@/Plugins/mad_useChildDataOptions";
import { useGetDataQuery } from "@/utils/react-query-hooks/getData";
import {
  WidgetEmptyState,
  WidgetErrorState,
  WidgetLoadingState,
} from "../../../../WidgetRenderer/comps/altStatePlaceholders";
import { useUpdateLoadedWidgetsTracker } from "../../../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import {
  ASSESSMENTS,
  MAX_DATASETS,
  TENANT,
  getTenantSettings,
} from "./settings";

export { ChildProgressConfigForm as ConfigForm } from "./ChildProgressConfigForm";

/**
 * MAD - Child Progress — one line chart per child of a volunteer, in a grid.
 *
 * Each chart: x axis Baseline → Endline, one line per series in settings.js
 * (the child's current grade, and the learning level each assessment
 * template records). The volunteer is the widget's one input — picked in the
 * config, or injected by the Volunteer Dashboard, which scopes every widget
 * on that report to the volunteer being viewed. The report's own filters are
 * ignored on purpose: this is a fixed lens on one volunteer's children.
 *
 * Which children: the volunteer's active allocations (the same lookup the
 * assessment forms' Child Name dropdown makes), plus any child that has
 * assessment docs under this volunteer but is no longer allocated — an
 * assessed child should not vanish from the dashboard when the allocation
 * ends.
 *
 * Data: one getData query per dataset, fetching the volunteer's published
 * docs across all their children in one go, grouped by child here. Hooks
 * cannot be called in a loop, so the query slots are fixed (MAX_DATASETS)
 * and the unused ones are switched off.
 */
const useDatasetQueries = ({ settings, volunteerId, enabled }) =>
  Object.entries(settings?.datasets || {})
    .concat(Array(MAX_DATASETS).fill(null))
    .slice(0, MAX_DATASETS)
    .map((entry) => {
      const [contentType] = entry || [];
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const query = useGetDataQuery({
        contentTypes: contentType ? [contentType] : [],
        findQuery: contentType
          ? {
              kp_published_status: "published",
              [`tags.${settings.volunteerTagType}.data._id`]: volunteerId,
            }
          : undefined,
        limit: 500,
        queryOptions: { enabled: enabled && !!contentType },
      });
      return { contentType, query };
    })
    .filter((slot) => slot.contentType);

const byPublishDate = (a, b) =>
  new Date(a.kp_date_published || 0) - new Date(b.kp_date_published || 0);

// The dropdown saves the child id as a number; be safe about docs that carry
// it as a string.
const childKey = (value) => String(value);

/**
 * { [childKey]: { display, points, hasData } } — one entry per child that has
 * a doc in any dataset. A doc's assessment number places its value on the x
 * axis; two docs for the same assessment resolve to the later publish.
 */
const groupByChild = ({ settings, slots }) => {
  const children = {};
  const childOf = (doc, dataset) => {
    const key = childKey(getVal(doc, dataset.childValuePath));
    if (!children[key]) {
      children[key] = {
        display: getVal(doc, dataset.childDisplayPath) || key,
        points: ASSESSMENTS.map((a) => ({ assessment: a.display })),
        hasData: false,
        // when the child's most recent assessment, in any template, was published
        lastAssessedAt: null,
      };
    }
    return children[key];
  };
  for (const series of settings.series) {
    const dataset = settings.datasets[series.contentType];
    const docs = [
      ...(slots.find((s) => s.contentType === series.contentType)?.query.data
        ?.data || []),
    ].sort(byPublishDate);
    for (const doc of docs) {
      const idx = ASSESSMENTS.findIndex(
        (a) => a.value === getVal(doc, dataset.assessmentValuePath),
      );
      const value = getVal(doc, series.valuePath);
      if (idx === -1 || typeof value !== "number") continue;
      const child = childOf(doc, dataset);
      child.points[idx][series.key] = value;
      child.hasData = true;
      if (
        doc.kp_date_published &&
        (!child.lastAssessedAt ||
          new Date(doc.kp_date_published) > new Date(child.lastAssessedAt))
      ) {
        child.lastAssessedAt = doc.kp_date_published;
      }
    }
  }
  return children;
};

// No border: the cards sit inside the widget's own card, and a frame around
// each chart read as a box in a box.
const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

// The chart draws its y-axis labels in a fixed-width column at the left of
// the plot, so the child's name is indented by that column and sits over the
// axis line, where the eye starts reading the chart.
const ChildName = styled.div`
  padding-left: ${_base_linechart_ui_configs.marginLeft + _base_linechart_ui_configs.yAxis.width}px;
`;

const CHART_HEIGHT = 220;
// Grades run 1–8; a shared 0–10 axis keeps every child's chart comparable.
const Y_MAX = 10;
// The chart's wrapper carries a 500px minHeight of its own (its default is
// to fill a full widget); a card in a grid wants exactly the chart's height.
const CHART_STYLE = { minHeight: 0, height: CHART_HEIGHT };

export const Component = ({ id, widgetIdx, volunteer }) => {
  const settings = getTenantSettings();
  const ready = !!settings && !!volunteer?._id;

  // The volunteer's currently allocated children — same call the forms make.
  const { options: allocated, status: allocationStatus } =
    mad_useChildDataOptions({
      content: {
        tags: {
          [settings?.volunteerTagType]: { data: volunteer ? [volunteer] : [] },
        },
      },
      readOnly: !ready,
    });
  const slots = useDatasetQueries({
    settings,
    volunteerId: volunteer?._id,
    enabled: ready,
  });

  const status = !ready
    ? "success"
    : slots.some((s) => s.query.status === "error")
      ? "error"
      : slots.some((s) => s.query.status === "loading") ||
          allocationStatus === "loading"
        ? "loading"
        : "success";
  const error = slots.find((s) => s.query.status === "error")?.query.error;

  const cards = useMemo(() => {
    if (status !== "success" || !ready) return [];
    const assessed = groupByChild({ settings, slots });
    const allocatedKeys = new Set();
    const fromAllocations = (allocated || [])
      .filter((child) => !child.loading)
      .map((child) => {
        const key = childKey(child.value);
        allocatedKeys.add(key);
        return {
          key,
          display: child.display,
          points:
            assessed[key]?.points ||
            ASSESSMENTS.map((a) => ({ assessment: a.display })),
          hasData: !!assessed[key]?.hasData,
          lastAssessedAt: assessed[key]?.lastAssessedAt || null,
        };
      });
    const assessedOnly = Object.entries(assessed)
      .filter(([key]) => !allocatedKeys.has(key))
      .map(([key, child]) => ({ key, ...child, formerlyAllocated: true }));
    return [...fromAllocations, ...assessedOnly];
  }, [status, ready, settings, slots, allocated]);

  const valuePath = useMemo(
    () => settings?.series.map((s) => s.key) || [],
    [settings],
  );
  const tooltipLabelsMapping = useMemo(
    () =>
      Object.fromEntries((settings?.series || []).map((s) => [s.key, s.label])),
    [settings],
  );

  useUpdateLoadedWidgetsTracker({
    status,
    widgetIdx,
    hasData: cards.some((c) => c.hasData),
    widgetId: id,
  });

  if (!settings)
    return (
      <WidgetEmptyState display="This widget is not set up for this platform" />
    );
  if (status === "loading") return <WidgetLoadingState />;
  if (status === "error") return <WidgetErrorState error={error} />;
  if (!cards.length)
    return (
      <WidgetEmptyState
        display={`No children under ${volunteer?.display || "this volunteer"}`}
      />
    );

  // No height cap and no inner scroller, unlike the other custom widgets: the
  // widget is as tall as its grid of charts. It sits on a full-width row of
  // its own, so nothing beside it stretches to match.
  return (
    <div style={{ padding: "1.6rem" }}>
      <WrapperCardGrid minWidth="34rem">
        {cards.map((child, i) => (
          <Card key={child.key}>
            <ChildName>
              <UI_BODY_SEMIBOLD_SM>{child.display}</UI_BODY_SEMIBOLD_SM>
              {child.lastAssessedAt && (
                <UI_CAPTION style={{ color: colors2.grey60, display: "block" }}>
                  Time since last assessment:{" "}
                  {moment(child.lastAssessedAt).fromNow(true)}
                </UI_CAPTION>
              )}
              {child.formerlyAllocated && (
                <UI_CAPTION style={{ color: colors2.grey60, display: "block" }}>
                  No longer allocated
                </UI_CAPTION>
              )}
            </ChildName>
            {child.hasData ? (
              <LineChart
                data={child.points}
                xAxisPath="assessment"
                valuePath={valuePath}
                tooltipLabelsMapping={tooltipLabelsMapping}
                // five named stages, not a time series: every tick, no thinning
                smartTicks={false}
                downsample={false}
                responsive={false}
                height={CHART_HEIGHT}
                style={CHART_STYLE}
                yMax={Y_MAX}
                // every child's chart colours its series the same way
                colorIdx={0}
              />
            ) : (
              <UI_CAPTION
                style={{
                  color: colors2.grey60,
                  display: "block",
                  minHeight: CHART_HEIGHT,
                }}
              >
                No published assessments yet
              </UI_CAPTION>
            )}
          </Card>
        ))}
      </WrapperCardGrid>
    </div>
  );
};

// MAD's widget, on MAD only — no other tenant sees it in the picker.
export const optionDef = {
  display: "MAD - Child Progress",
  icon: "Activity",
  desc: "A volunteer's children, one line chart each: current grade and learning levels, Baseline to Endline.",
  isAvailableToAdd: () => getTenant() === TENANT,
};

export const defaultConfig = { props: {} };

export const someReqPropsUnset = (widgetConfig) =>
  !widgetConfig?.props?.volunteer?._id;

// The container appends this to the card's title: "MAD - Child Progress — Utkarsh Mishra",
// and hands the templates this widget reads to the report's filter sidebar.
export const processRawConfigFn = (widgetConfig) => ({
  ...widgetConfig,
  props: {
    ...widgetConfig.props,
    titleSuffix: widgetConfig.props?.volunteer?.display,
    contentTypes: Object.keys(getTenantSettings()?.datasets || {}),
  },
});
